import {
  Compass,
  FileCheck,
  FileUp,
  Layers,
  Loader2,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserRound,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Page } from "@/components/layout/page"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { api } from "@/lib/api"
import { CV_ACCEPT, cvUploadBody } from "@/lib/cv-upload"
import type { Profile, ResumeSlot, RolesSummary } from "@/lib/types"
import { formatRelative } from "@/lib/utils"
import { ALL_PACKS, PACK_LABEL } from "@/lib/packs"
import { useTracks } from "@/hooks/use-tracks"

/**
 * Whose CV the assistant is answering about.
 *
 * Without this page the knowledge base is whatever was put in `data/kb/resumes`
 * by hand, which made the application honest but personal: it answered about
 * one engineer, and anyone else was looking at a demo. Dropping a CV in here
 * changes what every other part of the system reads - retrieval, fit scoring,
 * tailoring, cover letters, interview practice.
 *
 * The badge on each slot matters more than it looks. A profile with no real CV
 * falls back to a committed sample describing a *fictional* person, and an
 * answer about "your experience" drawn from that is worse than no answer. The
 * page says which is which before it says anything else.
 */

const PROFILE_LABEL = PACK_LABEL

/** The id used for the permanently empty row at the bottom of the shelf. */
const NEW_GENERAL = "__new_general__"

export function ResumesRoute() {
  const [slots, setSlots] = useState<ResumeSlot[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const { summary, reload: reloadRoles } = useTracks()

  async function refresh() {
    try {
      setSlots(await api.resumes.list())
    } catch (error) {
      setMessage((error as Error).message)
    }
  }

  // The first read is written out rather than calling `refresh`: state is set
  // only in the request's callbacks, and a response that arrives after the
  // page has gone is dropped.
  useEffect(() => {
    let cancelled = false
    api.resumes
      .list()
      .then((value) => !cancelled && setSlots(value))
      .catch((error: Error) => !cancelled && setMessage(error.message))
    return () => {
      cancelled = true
    }
  }, [])

  async function upload(profile: Profile, file: File, rowId: string) {
    setBusy(rowId)
    setMessage(null)
    try {
      const result = await api.resumes.upload(await cvUploadBody(profile, file))
      setMessage(result.message)
      await refresh()
    } catch (error) {
      setMessage((error as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function remove(slot: ResumeSlot) {
    setBusy(slot.id)
    setMessage(null)
    try {
      const result =
        slot.profile === "general"
          ? await api.resumes.removeGeneral(slot.id)
          : await api.resumes.remove(slot.profile)
      setMessage(result.message)
      await refresh()
    } catch (error) {
      setMessage((error as Error).message)
    } finally {
      setBusy(null)
    }
  }

  return (
    <Page
      tone="data"
      title="Your CVs"
      description="One CV per career track, private to your account. Scoring, tailoring and interview practice all read from these."
    >
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-4">
        <RolesEditor
          summary={summary}
          onChanged={() => {
            reloadRoles()
            void refresh()
          }}
          onMessage={setMessage}
        />

        {message ? (
          <p className="rounded-md border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
            {message}
          </p>
        ) : null}

        {slots === null ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <>
            {slots.map((slot) => (
              <Slot
                key={`${slot.profile}:${slot.id}`}
                slot={slot}
                busy={busy === slot.id}
                onUpload={(file) => upload(slot.profile, file, slot.id)}
                onRemove={() => remove(slot)}
              />
            ))}
            {/* Always last, always empty. The shelf has no fixed size, so the
                way to add to it is a row that is permanently available rather
                than a button that appears once the previous upload finished. */}
            <AddGeneral
              busy={busy === NEW_GENERAL}
              onUpload={(file) => upload("general", file, NEW_GENERAL)}
            />
          </>
        )}

        <p className="pt-2 text-xs leading-relaxed text-muted-foreground">
          Word, PDF, Markdown or plain text. The text is stored in your account and searched only
          for you; the parts a question, a score or a tailoring needs are sent to the AI model.
        </p>
        </div>

        <aside>
          <div className="xl:sticky xl:top-6">
            <ProfilePanel />
          </div>
        </aside>
      </div>
    </Page>
  )
}

/**
 * The roles the user is aiming for, in their own words.
 *
 * A title is typed freely; the pack it points to is suggested by the server
 * as the user types (`/api/roles/suggest`) and can be changed before adding.
 * Adding or removing a role changes the tracks every other page offers, so
 * the page reloads its CV slots afterwards.
 */
function RolesEditor({
  summary,
  onChanged,
  onMessage,
}: {
  summary: RolesSummary | null
  onChanged: () => void
  onMessage: (text: string | null) => void
}) {
  const [title, setTitle] = useState("")
  const [pack, setPack] = useState<Profile | "">("")
  const [suggested, setSuggested] = useState<Profile | null>(null)
  const [saving, setSaving] = useState(false)

  // Ask which pack the title points to, a moment after typing stops.
  useEffect(() => {
    const words = title.trim()
    if (words.length < 3) return
    let live = true
    const timer = window.setTimeout(() => {
      api.roles
        .suggest(words)
        .then((result) => {
          if (live) setSuggested(result.profile)
        })
        .catch(() => undefined)
    }, 350)
    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [title])

  const roles = summary?.roles ?? []
  const full = summary !== null && roles.length >= summary.maxRoles
  const chosen: Profile | null = pack || (title.trim().length >= 3 ? suggested : null)

  // Promise chains rather than try/catch: React Compiler skips a component
  // whose handlers contain a try block (docs/weaknesses.md, W20).
  function add() {
    const words = title.trim()
    if (words.length < 2 || saving) return
    setSaving(true)
    onMessage(null)
    api.roles
      .add(words, chosen ?? undefined)
      .then((role) => {
        setTitle("")
        setPack("")
        setSuggested(null)
        onMessage(
          role.profile === "general"
            ? `Added "${role.title}". No expert pack fits it, so it works from your CV and the career guides - and job searches use "${role.title}" itself. A German title (for example "Pflegefachkraft") finds more on the Bundesagentur.`
            : `Added "${role.title}" - it uses the ${role.packLabel} pack.`,
        )
        onChanged()
      })
      .catch((caught: Error) => onMessage(caught.message))
      .finally(() => setSaving(false))
  }

  function remove(id: string) {
    api.roles
      .remove(id)
      .then(onChanged)
      .catch((caught: Error) => onMessage(caught.message))
  }

  return (
    <section className="space-y-3 rounded-xl border bg-card/40 p-5" aria-labelledby="target-roles">
      <div>
        <h2 id="target-roles" className="font-medium">
          The roles you are aiming for
        </h2>
        <p className="text-sm text-muted-foreground">
          {roles.length === 0
            ? "Start here: name one to three roles in your own words. Each gets its own CV slot below."
            : "Each role uses an expert pack; the CV slots below follow them."}
        </p>
      </div>

      {roles.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {roles.map((role) => (
            <li
              key={role.id}
              className="inline-flex items-center gap-2 rounded-full border bg-background/60 py-1 pl-3 pr-1 text-sm"
            >
              <span className="font-medium">{role.title}</span>
              <Badge variant={role.profile === "general" ? "secondary" : "outline"}>
                {role.packLabel}
              </Badge>
              <button
                type="button"
                onClick={() => remove(role.id)}
                aria-label={`Remove ${role.title}`}
                className="grid size-6 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Trash2 className="size-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {full ? (
        <p className="text-xs text-muted-foreground">
          That is the most roles one account keeps. Remove one to add another.
        </p>
      ) : (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            add()
          }}
        >
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="e.g. Senior Backend Engineer"
            aria-label="Role title"
            maxLength={120}
            className="h-9 min-w-[14rem] flex-1 rounded-md border bg-background px-3 text-sm"
          />
          <select
            value={pack || chosen || ""}
            onChange={(event) => setPack(event.target.value as Profile | "")}
            aria-label="Expert pack"
            className="h-9 rounded-md border bg-background px-3 text-sm"
          >
            <option value="">Pack: decide from the title</option>
            {ALL_PACKS.map((value) => (
              <option key={value} value={value}>
                {PACK_LABEL[value]}
              </option>
            ))}
          </select>
          <Button type="submit" size="sm" disabled={saving || title.trim().length < 2}>
            {saving ? <Loader2 className="animate-spin" aria-hidden /> : <Plus aria-hidden />}
            Add role
          </Button>
        </form>
      )}
    </section>
  )
}

/**
 * What a profile is, and the two questions this page kept being asked.
 *
 * Both have the same root: the tracks are a closed set, not a folder of files.
 * `Profile` is an enum, every tool takes one of its members, and the knowledge
 * base has one directory per member. So the page offers replacement and not
 * addition, and saying that plainly is better than leaving someone to hunt for
 * an "add" button that was never going to be there.
 *
 * This panel said "exactly three" until a fourth was added, which is the risk
 * with copy that counts things. It now describes the shape rather than the
 * number, and the number it does quote comes from the slots the API returned.
 */
function ProfilePanel() {
  return (
    <div className="space-y-4 rounded-2xl border bg-card/40 p-5">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
          How this works
        </p>
        <p className="mt-1 text-lg font-medium">One CV per track</p>
      </div>

      <Note
        icon={<Layers className="size-4" aria-hidden />}
        title="Roles and expert packs"
        body="Name the roles you are aiming for in your own words. Each one uses an expert pack - specialist notes, job sorting and interview topics - or General when none fits, which works from your CV and the career guides."
      />
      <Note
        icon={<Compass className="size-4" aria-hidden />}
        title="General holds several"
        body="Each track takes one CV and replacing overwrites. General is a shelf: add as many as you like, each named after its first heading. Tailoring uses the most recent, marked In use."
      />
      <Note
        icon={<RefreshCw className="size-4" aria-hidden />}
        title="Word, PDF or Markdown"
        body="Read, split into passages and made searchable in a few seconds. A scanned PDF has no text to read - export a text PDF or a Word file instead."
      />
      <Note
        icon={<FileCheck className="size-4" aria-hidden />}
        title="Tailored CVs go elsewhere"
        body="Approved documents are kept on the Documents page, to download as Word or Markdown. These are the sources they are written from."
      />
      <Note
        icon={<ShieldCheck className="size-4" aria-hidden />}
        title="Private to you"
        body="Your CVs are stored against your account. No other account's search, scoring or tailoring can reach them."
      />
    </div>
  )
}

function Note({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg border border-border/70 bg-background/50 text-accent">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{body}</p>
      </div>
    </div>
  )
}

/**
 * The permanently empty row at the bottom of the General shelf.
 *
 * Not a button that appears when the shelf is empty and vanishes when it is
 * not: the shelf has no fixed size, so there is never a moment when adding to
 * it stops making sense. A row that is always there is also the only affordance
 * on this page that does not look like it might overwrite something.
 */
function AddGeneral({ busy, onUpload }: { busy: boolean; onUpload: (file: File) => void }) {
  const input = useRef<HTMLInputElement>(null)

  return (
    <button
      type="button"
      onClick={() => input.current?.click()}
      disabled={busy}
      className="flex w-full items-center gap-3 rounded-lg border border-dashed bg-card/20 px-5 py-4 text-left transition-colors hover:border-border hover:bg-card/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
    >
      <input
        ref={input}
        type="file"
        accept={CV_ACCEPT}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) onUpload(file)
          event.target.value = ""
        }}
      />
      <span className="grid size-8 shrink-0 place-items-center rounded-lg border border-dashed text-muted-foreground">
        {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium">
          {busy ? "Indexing\u2026" : "Add another CV"}
        </span>
        <span className="block text-xs text-muted-foreground">
          Goes on the General shelf.
        </span>
      </span>
    </button>
  )
}

function Slot({
  slot,
  busy,
  onUpload,
  onRemove,
}: {
  slot: ResumeSlot
  busy: boolean
  onUpload: (file: File) => void
  onRemove: () => void
}) {
  const input = useRef<HTMLInputElement>(null)

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border bg-card/40 px-5 py-4">
      <div className="min-w-0 space-y-1">
        <p className="flex items-center gap-2 font-medium">
          <UserRound className="size-4 shrink-0 text-muted-foreground" />
          {slot.profile === "general" ? slot.label : PROFILE_LABEL[slot.profile]}
          {slot.profile === "general" ? <Badge variant="outline">General</Badge> : null}
          {slot.inUse ? <Badge>In use</Badge> : null}
          {slot.isYours ? (
            <Badge variant="outline">Yours</Badge>
          ) : (
            <Badge variant="secondary">No CV yet</Badge>
          )}
        </p>
        <p className="text-sm text-muted-foreground">
          {slot.isYours
            ? `${slot.characters.toLocaleString()} characters${
                slot.updatedAt ? ` · updated ${formatRelative(slot.updatedAt)}` : ""
              }`
            : "No CV of yours here yet. Upload one to score jobs and tailor documents for this track."}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <input
          ref={input}
          type="file"
          accept={CV_ACCEPT}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) onUpload(file)
            event.target.value = ""
          }}
        />
        {slot.isYours ? (
          <Button variant="ghost" size="sm" onClick={onRemove} disabled={busy}>
            <Trash2 className="mr-2 size-4" />
            Remove
          </Button>
        ) : null}
        <Button variant="outline" size="sm" onClick={() => input.current?.click()} disabled={busy}>
          {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : <FileUp className="mr-2 size-4" />}
          {slot.isYours ? "Replace" : "Upload"}
        </Button>
      </div>
    </div>
  )
}
